import {$} from "jquery";
import assert from "minimalistic-assert";

import {electron_bridge} from "./electron_bridge.ts";
import type {Message} from "./message_store.ts";

type NoticeMemory = Map<
    string,
    {
        obj: Notification | ElectronBridgeNotification;
        msg_count: number;
        message_id: number;
    }
>;

export const notice_memory: NoticeMemory = new Map();

export let NotificationAPI: typeof ElectronBridgeNotification | typeof Notification | undefined;

// Used for testing
export function set_notification_api(n: typeof NotificationAPI): void {
    NotificationAPI = n;
}

export class ElectronBridgeNotification extends EventTarget {
    title: string;
    dir: NotificationDirection;
    lang: string;
    body: string;
    tag: string;
    icon: string;
    data: unknown;
    close: () => void;

    constructor(title: string, options: NotificationOptions) {
        super();
        assert(electron_bridge?.new_notification !== undefined);
        const notification_data = electron_bridge.new_notification(
            title,
            options,
            (type, eventInit) => this.dispatchEvent(new Event(type, eventInit)),
        );
        this.title = notification_data.title;
        this.dir = notification_data.dir;
        this.lang = notification_data.lang;
        this.body = notification_data.body;
        this.tag = notification_data.tag;
        this.icon = notification_data.icon;
        this.data = notification_data.data;
        this.close = notification_data.close;
    }

    static get permission(): NotificationPermission {
        return Notification.permission;
    }

    static async requestPermission(
        callback?: (permission: NotificationPermission) => void,
    ): Promise<NotificationPermission> {
        if (callback) {
            callback(await Promise.resolve(Notification.permission));
        }
        return Notification.permission;
    }
}

// Most mobile browsers expose the Notification interface, but
// support only persistent notifications, which are created via
// ServiceWorkerRegistration.showNotification().
// The Notification() constructor throws a TypeError there, so until
// we register a service worker, treat them as having no support.
// See https://developer.mozilla.org/en-US/docs/Web/API/Notifications_API#persistent_and_non-persistent_notifications
export function create_notification(
    title: string,
    options: NotificationOptions,
): ElectronBridgeNotification | Notification | undefined {
    if (NotificationAPI === undefined) {
        return undefined;
    }
    try {
        return new NotificationAPI(title, options);
    } catch (error) {
        if (!(error instanceof TypeError)) {
            throw error;
        }
        NotificationAPI = undefined;
        return undefined;
    }
}

if (electron_bridge?.new_notification) {
    NotificationAPI = ElectronBridgeNotification;
} else if (window.Notification) {
    NotificationAPI = window.Notification;
    if (Notification.permission !== "granted") {
        // A notification constructed without permission is never
        // displayed, which lets us check the constructor without
        // bothering the user. Once permission has been granted we
        // cannot make this check, so we treat the browser as supported
        // until a notification first fails to be constructed.
        create_notification("", {})?.close();
    }
}

export function get_notifications(): NoticeMemory {
    return notice_memory;
}

export function initialize(): void {
    $(window).on("focus", () => {
        for (const notice_mem_entry of notice_memory.values()) {
            notice_mem_entry.obj.close();
        }
        notice_memory.clear();
    });
}

// Utility function for testing support for the Notifications API
export function has_notification_support(): boolean {
    return NotificationAPI !== undefined;
}

export function permission_state(): string {
    if (NotificationAPI) {
        return NotificationAPI.permission;
    }
    // Act like notifications are blocked if a UA
    // has no access to the notification API.
    return "denied";
}

export function close_notification(message: Message): void {
    for (const [key, notice_mem_entry] of notice_memory) {
        if (notice_mem_entry.message_id === message.id) {
            notice_mem_entry.obj.close();
            notice_memory.delete(key);
        }
    }
}

export function granted_desktop_notifications_permission(): boolean {
    return NotificationAPI?.permission === "granted";
}

export async function request_desktop_notifications_permission(): Promise<NotificationPermission> {
    if (NotificationAPI) {
        return await NotificationAPI.requestPermission();
    }
    // Act like notifications are blocked if a UA
    // has no access to the notification API.
    return "denied";
}
